import type {
  detectOverflow,
  DetectOverflowOptions,
  Middleware,
  MiddlewareData,
  Placement,
  ShiftOptions,
  UseFloatingReturn,
} from '@floating-ui/react-dom';
import {
  arrow,
  autoUpdate,
  flip,
  hide,
  limitShift,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import { Slot } from '@radix-ui/react-slot';
import classNames from 'classnames';
import type { PrimitiveAtom } from 'jotai';
import { atom, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import React, {
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import Sizes from '../utils/responsive/Sizes';
import isSize from '../utils/responsive/isSize';
import * as styles from './PopOverPrimitive.module.scss';
import { isTouchDevice } from '../constants/os';

const DETECT_OVERFLOW_OPTIONS: Partial<
  Parameters<typeof detectOverflow>[1] & ShiftOptions
> = {
  padding: 0,
  boundary: [],
  altBoundary: false,
  mainAxis: true,
  crossAxis: false,
  limiter: limitShift(),
};

type TriggerType = 'click' | 'hover' | 'clickOrHover' | 'none';

interface PortalContainerProps {
  children: React.ReactNode;
  noPortal: boolean | undefined;
}

const PortalContainer = ({ children, noPortal }: PortalContainerProps) => {
  if (noPortal) return <>{children}</>;

  return createPortal(children, document.body);
};

const OPEN_DELAY_DURATION = 1000;
const CLOSE_DELAY_DURATION = 500;

type StateAtom =
  | {
      id: string;
      state: 'open';
    }
  | {
      id: string;
      state: 'hoverOut';
      cancel: () => void;
    }
  | {
      id?: string;
      nextId: string;
      state: 'hoverIn';
      cancel: () => void;
    }
  | { state: 'idle' };

const atomMap = new Map<string, ReturnType<typeof getOrCreateStateAtom>>();

const getOrCreateStateAtom = (namespace: string): PrimitiveAtom<StateAtom> => {
  const currentStateAtom = atomMap.get(namespace);
  if (currentStateAtom) return currentStateAtom;

  const stateAtom = atom<StateAtom>({ state: 'idle' });
  atomMap.set(namespace, stateAtom);
  return stateAtom;
};

interface PopOverContextValue {
  onMouseEnter: React.MouseEventHandler;
  onMouseLeave: React.MouseEventHandler;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  id: string;
  trigger: TriggerType;
  floatingStyles: React.CSSProperties;
  refs: UseFloatingReturn['refs'];
  actualPlacement: Placement;
  arrowData: MiddlewareData['arrow'];
  arrowRef: React.RefObject<SVGSVGElement>;
  hideData: MiddlewareData['hide'];
  shouldUseTouch: boolean;
}

const PopOverContext = React.createContext<PopOverContextValue | null>(null);
const useOptionalPopOverContext = () => useContext(PopOverContext);

const usePopOverContext = () => {
  const context = useOptionalPopOverContext();
  if (!context) {
    throw new Error(
      'PopOverPrimitive compound components cannot be rendered outside the PopOverPrimitive component',
    );
  }
  return context;
};

export type PopOverPrimitiveAllowControlledProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
    }
  | {
      open?: never;
      onOpenChange?: never;
    };

type PopOverPrimitiveProps = {
  children: React.ReactNode;
  trigger?: TriggerType;
  /**  @deprecated use enabledForTouch instead */
  enabledForSmallSize?: boolean;
  enabledForTouch?: boolean;
  offset?: number;
  placement?: Placement;
  namespace: string;
  autoUpdateAnimationFrame?: boolean;
  screenPadding?: number;
  noFlip?: boolean;
  hideWhenReferenceHidden?: boolean;
  openDelayDuration?: number;
  closeDelayDuration?: number;
  getAdditionalMiddleware?: (
    options: Partial<DetectOverflowOptions>,
  ) => Middleware[];
} & PopOverPrimitiveAllowControlledProps;

const PopOverPrimitive = ({
  children,
  trigger = 'hover',
  enabledForSmallSize,
  enabledForTouch,
  offset: offsetAmount,
  placement = 'top',
  namespace,
  onOpenChange,
  open,
  autoUpdateAnimationFrame,
  screenPadding,
  noFlip,
  hideWhenReferenceHidden,
  openDelayDuration = OPEN_DELAY_DURATION,
  closeDelayDuration = CLOSE_DELAY_DURATION,
  getAdditionalMiddleware,
}: PopOverPrimitiveProps) => {
  const id = useId();
  const arrowRef = useRef<SVGSVGElement | null>(null);
  const isMobile = isSize(Sizes.SM);

  let actualTrigger = trigger;
  if (isMobile && trigger === 'hover' && enabledForSmallSize) {
    actualTrigger = 'click';
  }

  const shouldUseTouch = (isTouchDevice && enabledForTouch) ?? false;
  if (shouldUseTouch && trigger === 'hover') {
    actualTrigger = 'click';
  }

  const isHoverActionAllowed =
    actualTrigger === 'hover' || actualTrigger === 'clickOrHover';

  const middleware = useMemo(() => {
    const overflowOptions = {
      ...DETECT_OVERFLOW_OPTIONS,
      padding: screenPadding,
    };

    return [
      offset(offsetAmount),
      shift(overflowOptions),
      noFlip
        ? undefined
        : flip({
            fallbackAxisSideDirection: 'end',
            ...DETECT_OVERFLOW_OPTIONS,
          }),

      arrow({ element: arrowRef, padding: 8 }),
      ...(getAdditionalMiddleware
        ? getAdditionalMiddleware(overflowOptions)
        : []),

      // Hide should always be placed at the end
      // https://floating-ui.com/docs/hide#order
      hideWhenReferenceHidden ? hide() : undefined,
    ];
  }, [
    offsetAmount,
    arrowRef,
    screenPadding,
    noFlip,
    hideWhenReferenceHidden,
    getAdditionalMiddleware,
  ]);

  const {
    refs,
    floatingStyles,
    middlewareData: { arrow: arrowData, hide: hideData },
    placement: actualPlacement,
  } = useFloating({
    placement,
    middleware,
    whileElementsMounted(referenceEl, floatingEl, update) {
      const cleanup = autoUpdate(referenceEl, floatingEl, update, {
        animationFrame: autoUpdateAnimationFrame,
      });
      return cleanup;
    },
  });

  const onOpenChangeRef = useRef(onOpenChange);
  useLayoutEffect(() => {
    onOpenChangeRef.current = onOpenChange;
  }, [onOpenChange]);

  const setIsOpen = useAtomCallback(
    useCallback(
      (get, set, isOpen: boolean) => {
        if (onOpenChangeRef.current) {
          onOpenChangeRef.current(isOpen);
          return;
        }
        const stateAtom = getOrCreateStateAtom(namespace);
        const currentState = get(stateAtom);
        if (
          currentState.state === 'hoverIn' ||
          currentState.state === 'hoverOut'
        ) {
          currentState.cancel();
        }

        if (isOpen) {
          set(stateAtom, { state: 'open', id });
        } else {
          set(stateAtom, { state: 'idle' });
        }
      },
      [id, namespace],
    ),
  );

  const onMouseEnter = useAtomCallback(
    useCallback(
      (get, set) => {
        if (!isHoverActionAllowed) return;
        const stateAtom = getOrCreateStateAtom(namespace);
        const currentState = get(stateAtom);

        if (currentState.state === 'hoverOut') {
          currentState.cancel();
          set(stateAtom, { state: 'open', id });
          return;
        }

        const currentId =
          currentState.state === 'idle' ? undefined : currentState.id;

        const timeout = setTimeout(() => {
          set(stateAtom, { state: 'open', id });
        }, openDelayDuration);

        set(stateAtom, {
          state: 'hoverIn',
          id: currentId,
          nextId: id,
          cancel: () => {
            clearTimeout(timeout);
          },
        });
      },
      [id, isHoverActionAllowed, namespace, openDelayDuration],
    ),
  );

  const onMouseLeave = useAtomCallback(
    useCallback(
      (get, set) => {
        if (!isHoverActionAllowed) return;
        const stateAtom = getOrCreateStateAtom(namespace);
        const currentState = get(stateAtom);

        if (currentState.state === 'hoverIn') {
          currentState.cancel();
          if (currentState.id == null) {
            set(stateAtom, { state: 'idle' });
            return;
          }
        }

        const timeout = setTimeout(() => {
          set(stateAtom, { state: 'idle' });
        }, closeDelayDuration);

        set(stateAtom, {
          state: 'hoverOut',
          id,
          cancel: () => {
            clearTimeout(timeout);
          },
        });
      },
      [id, isHoverActionAllowed, namespace, closeDelayDuration],
    ),
  );

  const isPopOverOpenAtom = useMemo(
    () =>
      atom((get) => {
        const stateAtom = getOrCreateStateAtom(namespace);
        const value = get(stateAtom);
        return value.state !== 'idle' && value.id === id;
      }),
    [id, namespace],
  );

  const isOpen = useAtomValue(isPopOverOpenAtom);
  const isActuallyOpen = onOpenChange != null ? !!open : isOpen;

  const contextValue = useMemo(
    () => ({
      onMouseEnter,
      onMouseLeave,
      setIsOpen,
      isOpen: isActuallyOpen,
      id,
      trigger: actualTrigger,
      actualPlacement,
      floatingStyles,
      refs,
      arrowData,
      arrowRef,
      hideData,
      shouldUseTouch,
    }),
    [
      onMouseEnter,
      onMouseLeave,
      setIsOpen,
      isActuallyOpen,
      id,
      actualTrigger,
      actualPlacement,
      refs,
      floatingStyles,
      arrowData,
      arrowRef,
      hideData,
      shouldUseTouch,
    ],
  );

  return (
    <PopOverContext.Provider value={contextValue}>
      {children}
    </PopOverContext.Provider>
  );
};

const CLICK_EVENTS = new WeakSet<Event>();

interface PopOverTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
  /**  @deprecated use asChild instead */
  display?: 'block' | 'inline-block' | 'inline';
}

const PopOverTrigger = ({
  children,
  asChild,
  display,
}: PopOverTriggerProps) => {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const {
    id,
    onMouseEnter,
    onMouseLeave,
    shouldUseTouch,
    trigger,
    isOpen,
    setIsOpen,
    refs,
  } = usePopOverContext();

  useLayoutEffect(() => {
    // If it's not null, then it's already set through PopOverAnchor
    if (refs.reference.current == null) {
      refs.setReference(triggerRef.current);
    }
  }, [refs]);

  const commonProps = {
    onMouseEnter,
    onMouseLeave,
    ref: triggerRef,
    'aria-describedby': id,
  };

  const triggerContainerClassName = classNames(
    styles.triggerContainer,
    asChild
      ? {}
      : {
          [styles.inline]: display === 'inline',
          [styles.block]: display === 'block',
        },
  );

  const onClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {trigger === 'hover' || trigger === 'none' ? (
        <>
          {asChild ? (
            <Slot {...commonProps}>{children}</Slot>
          ) : (
            <div {...commonProps} className={triggerContainerClassName}>
              {children}
            </div>
          )}
        </>
      ) : (
        <ClickOutsideHandler
          {...commonProps}
          onClickOutside={(e) => {
            if (
              isOpen &&
              !CLICK_EVENTS.has(e) &&
              !triggerRef.current?.contains(e.target as Node)
            ) {
              setIsOpen(false);
            }
          }}
          {...(shouldUseTouch
            ? {
                onTouchStart: onClick,
              }
            : {
                onClick,
              })}
          asChild={asChild}
          className={triggerContainerClassName}
        >
          {children}
        </ClickOutsideHandler>
      )}
    </>
  );
};

type PopOverAnchorProps = {
  children: React.ReactNode;
};

const PopOverAnchor = ({ children }: PopOverAnchorProps) => {
  const context = useOptionalPopOverContext();
  if (context == null) return children;

  return <Slot ref={context?.refs.setReference}>{children}</Slot>;
};

interface PopOverFloatingProps {
  children: React.ReactNode;
  className?: string;
  noPortal?: boolean;
  interactionDisabled?: boolean;
}

const PopOverFloating = ({
  children,
  className,
  noPortal,
  interactionDisabled,
}: PopOverFloatingProps) => {
  const {
    id,
    onMouseEnter,
    onMouseLeave,
    isOpen,
    refs,
    floatingStyles,
    hideData,
  } = usePopOverContext();

  return (
    <PortalContainer noPortal={noPortal}>
      {isOpen && (
        <div
          role="tooltip"
          id={id}
          ref={refs.setFloating}
          style={floatingStyles}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={classNames(styles.floatingContainer, className, {
            [styles.isReferenceHidden]: hideData?.referenceHidden,
            [styles.isInteractionDisabled]: interactionDisabled,
          })}
        >
          {children}
        </div>
      )}
    </PortalContainer>
  );
};

interface PopOverArrowProps
  extends Omit<React.SVGAttributes<SVGSVGElement>, 'style' | 'visibility'> {
  children: React.ReactNode;
  height: number;
  width: number;
  offset?: number;
}

const PopOverArrow = ({
  children,
  height,
  width,
  offset: offsetAmount = 0,
  ...rest
}: PopOverArrowProps) => {
  const { actualPlacement, arrowData, arrowRef } = usePopOverContext();

  const arrowPosition = arrowData
    ? { left: arrowData.x, top: arrowData.y }
    : {};

  const cssVars = {
    '--popover-arrow-height': `${height}px`,
    '--popover-arrow-width': `${width}px`,
    '--popover-arrow-offset': `${offsetAmount}px`,
  } as React.CSSProperties;

  return (
    <>
      <div
        className={styles.arrowHoverArea}
        data-placement={actualPlacement}
        style={cssVars}
      />
      <svg
        {...rest}
        data-placement={actualPlacement}
        className={classNames(styles.arrow, rest.className)}
        style={{
          ...arrowPosition,
          ...cssVars,
        }}
        visibility="visible"
        ref={arrowRef}
      >
        {children}
      </svg>
    </>
  );
};

interface PopOverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  hidden?: boolean;
}

const PopOverContent = React.forwardRef<HTMLDivElement, PopOverContentProps>(
  ({ children, hidden, ...rest }, ref) => {
    return (
      <div
        {...rest}
        className={classNames(styles.content, rest.className, {
          [styles.hidden]: hidden,
        })}
        onMouseDown={(e) => {
          CLICK_EVENTS.add(e.nativeEvent);
          rest.onMouseDown?.(e);
        }}
        onTouchStart={(e) => {
          CLICK_EVENTS.add(e.nativeEvent);
          rest.onTouchStart?.(e);
        }}
        ref={ref}
      >
        {children}
      </div>
    );
  },
);

PopOverPrimitive.Trigger = PopOverTrigger;
PopOverPrimitive.Floating = PopOverFloating;
PopOverPrimitive.Arrow = PopOverArrow;
PopOverPrimitive.Content = PopOverContent;
PopOverPrimitive.Anchor = PopOverAnchor;

export default PopOverPrimitive;
